import { useState, useEffect } from "react";
import { Search, Download, X, ChevronDown } from "lucide-react";
import axios from "axios";
import NavBar from "@/components/NavBar";
import { useToast } from "@/hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SelectField = ({ label, value, onChange, options, placeholder, testId }) => (
  <div>
    <label className="text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-1 block">
      {label}
    </label>
    <div className="relative">
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-12 bg-neutral-900 border border-neutral-800 rounded-md px-4 pr-10 text-neutral-50 focus:border-amber-400 focus:outline-none appearance-none text-sm"
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 pointer-events-none" />
    </div>
  </div>
);

const InventarioPage = () => {
  const { toast } = useToast();
  const [sedes, setSedes] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedSede, setSelectedSede] = useState("");
  const [selectedZona, setSelectedZona] = useState("");
  const [selectedFecha, setSelectedFecha] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [responsable, setResponsable] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [quantities, setQuantities] = useState({});
  const [isExporting, setIsExporting] = useState(false);
  const [filterByZona, setFilterByZona] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sedesRes, zonasRes, productsRes] = await Promise.all([
          axios.get(`${API}/sedes`, { params: { skip: 0, limit: 1000 } }),
          axios.get(`${API}/zonas`, { params: { skip: 0, limit: 1000 } }),
          axios.get(`${API}/productos/inventario`, { params: { skip: 0, limit: 1000 } }),
        ]);
        const sedes = sedesRes.data.items ?? sedesRes.data;
        const zonas = zonasRes.data.items ?? zonasRes.data;
        setSedes(sedes);
        setZonas(zonas);
        setProducts(productsRes.data.items ?? productsRes.data);
        if (sedes.length > 0) setSelectedSede(sedes[0].id);
        if (zonas.length > 0) setSelectedZona(zonas[0].id);
      } catch (err) {
        console.error("Error cargando datos:", err);
        toast({
          title: "Error al cargar datos",
          description: err.response?.status === 500
            ? "Error del servidor. Intenta más tarde."
            : "No se pudieron cargar los datos. Verifica tu conexión.",
          variant: "destructive",
        });
      }
    };
    fetchData();
  }, []);

  const updateQuantity = (productId, value) => {
    const normalizedValue = value === "" ? "" : value.replace(/[^0-9]/g, "");
    setQuantities((prev) => ({
      ...prev,
      [productId]: normalizedValue === "" ? "" : Number(normalizedValue),
    }));
  };

  // Filtro por zona (activo solo cuando filterByZona está activado)
  const zonaProducts = !filterByZona
    ? products
    : selectedZona
    ? products.filter((p) => p.zona_id === selectedZona)
    : [];

  // Filtro adicional por búsqueda de texto
  const filteredProducts = zonaProducts.filter((p) =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sedeName = sedes.find((s) => s.id === selectedSede)?.name || "—";
  const zonaName = zonas.find((z) => z.id === selectedZona)?.name || "—";
  const formattedDate = selectedFecha
    ? new Date(selectedFecha + "T12:00:00").toLocaleDateString("es-CO", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : "—";

  const exportExcel = async () => {
    setIsExporting(true);
    try {
      const ExcelJS = (await import("exceljs")).default;
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Sistema de Inventario";
      workbook.created = new Date();

      const ws = workbook.addWorksheet("Inventario");

      // --- Info rows (1-4) ---
      const infoStyle = { font: { size: 11 }, alignment: { vertical: "middle" } };
      const infoLabelStyle = { font: { bold: true, size: 11 }, alignment: { vertical: "middle" } };

      ws.getRow(1).getCell(1).value = "SEDE:";
      ws.getRow(1).getCell(2).value = sedeName;
      ws.getRow(2).getCell(1).value = "FECHA:";
      ws.getRow(2).getCell(2).value = formattedDate;
      ws.getRow(3).getCell(1).value = "ZONA:";
      ws.getRow(3).getCell(2).value = zonaName;
      ws.getRow(4).getCell(1).value = "RESPONSABLE:";
      ws.getRow(4).getCell(2).value = responsable || "—";

      [1, 2, 3, 4].forEach((rowNum) => {
        ws.getRow(rowNum).getCell(1).style = infoLabelStyle;
        ws.getRow(rowNum).getCell(2).style = infoStyle;
        ws.getRow(rowNum).height = 18;
      });

      // Row 5: separator
      ws.getRow(5).height = 6;

      // --- Column headers (row 6) ---
      ws.getRow(6).values = ["CÓDIGO", "PRODUCTO", "CANTIDAD ACTUAL", "ESTADO"];
      const headerRow = ws.getRow(6);
      headerRow.height = 26;
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF16A34A" },
        };
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.border = {
          top: { style: "thin", color: { argb: "FF14532D" } },
          bottom: { style: "thin", color: { argb: "FF14532D" } },
          left: { style: "thin", color: { argb: "FF14532D" } },
          right: { style: "thin", color: { argb: "FF14532D" } },
        };
      });

      // Set column widths
      ws.getColumn(1).width = 16;
      ws.getColumn(2).width = 42;
      ws.getColumn(3).width = 20;
      ws.getColumn(4).width = 14;

      // --- Data rows (from row 7): sólo productos de la zona seleccionada ---
      zonaProducts.forEach((p, index) => {
        const rawQty = quantities[p.id];
        const cantidad = rawQty !== "" && rawQty !== undefined ? Number(rawQty) : 0;
        const estado = cantidad > 0 ? "OK" : "AGOTADO";
        const dataRow = ws.getRow(7 + index);
        dataRow.values = [p.codigo || "—", p.name, cantidad, estado];
        dataRow.height = 20;

        // Alternating row fill
        const isEven = index % 2 === 0;
        [1, 2, 3, 4].forEach((colNum) => {
          const cell = dataRow.getCell(colNum);
          if (isEven) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFF9FAFB" },
            };
          }
          cell.border = {
            top: { style: "hair", color: { argb: "FFE5E7EB" } },
            bottom: { style: "hair", color: { argb: "FFE5E7EB" } },
            left: { style: "hair", color: { argb: "FFE5E7EB" } },
            right: { style: "hair", color: { argb: "FFE5E7EB" } },
          };
        });

        // Código column
        dataRow.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
        dataRow.getCell(1).font = { name: "Courier New", color: { argb: "FF9CA3AF" }, size: 10 };

        // Cantidad column
        dataRow.getCell(3).alignment = { horizontal: "center", vertical: "middle" };
        dataRow.getCell(3).font = {
          name: "Courier New",
          size: 11,
          bold: cantidad > 0,
        };

        // Estado column
        const estadoCell = dataRow.getCell(4);
        estadoCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: cantidad > 0 ? "FFD1FAE5" : "FFFEE2E2" },
        };
        estadoCell.font = {
          color: { argb: cantidad > 0 ? "FF065F46" : "FF7F1D1D" },
          bold: true,
          size: 10,
        };
        estadoCell.alignment = { horizontal: "center", vertical: "middle" };
      });

      // Freeze header row
      ws.views = [{ state: "frozen", ySplit: 6 }];

      // Auto filter on header row
      if (products.length > 0) {
        ws.autoFilter = {
          from: { row: 6, column: 1 },
          to: { row: 6 + products.length, column: 4 },
        };
      }

      // Download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const filename = `inventario_${selectedFecha || "fecha"}_${sedeName}.xlsx`.replace(/\s+/g, "_");
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error generando Excel:", err);
      toast({
        title: "Error al exportar",
        description: "No se pudo generar el archivo Excel. Intenta nuevamente.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div
      className="bg-neutral-950 flex flex-col max-w-md mx-auto relative"
      style={{ fontFamily: "'Outfit', sans-serif", minHeight: "100dvh" }}
    >
      <NavBar title="Inventario" subtitle="Control de stock" />

      {/* Header form */}
      <div className="px-4 pt-4 pb-3 space-y-3 border-b border-neutral-800 bg-neutral-950">
        <SelectField
          label="Sede"
          value={selectedSede}
          onChange={setSelectedSede}
          options={sedes}
          placeholder="— Seleccionar sede —"
          testId="inv-select-sede"
        />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-1 block">
              Fecha
            </label>
            <input
              data-testid="inv-input-fecha"
              type="date"
              value={selectedFecha}
              onChange={(e) => setSelectedFecha(e.target.value)}
              className="w-full h-12 bg-neutral-900 border border-neutral-800 rounded-md px-3 text-neutral-50 focus:border-amber-400 focus:outline-none text-sm"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold tracking-widest uppercase text-neutral-500">
                Zona
              </label>
              <button
                data-testid="inv-toggle-filter-zona"
                onClick={() => setFilterByZona((v) => !v)}
                className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
                  filterByZona
                    ? "bg-amber-400/15 text-amber-400"
                    : "bg-neutral-800 text-neutral-500"
                }`}
              >
                <span
                  className={`w-2 h-2 rounded-full inline-block transition-colors ${
                    filterByZona ? "bg-amber-400" : "bg-neutral-600"
                  }`}
                />
                Filtrar
              </button>
            </div>
            <div className="relative">
              <select
                data-testid="inv-select-zona"
                value={selectedZona}
                onChange={(e) => setSelectedZona(e.target.value)}
                className="w-full h-12 bg-neutral-900 border border-neutral-800 rounded-md px-4 pr-10 text-neutral-50 focus:border-amber-400 focus:outline-none appearance-none text-sm"
              >
                <option value="">— Zona —</option>
                {zonas.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 pointer-events-none" />
            </div>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-1 block">
            Responsable
          </label>
          <input
            data-testid="inv-input-responsable"
            type="text"
            placeholder="Nombre del responsable"
            value={responsable}
            onChange={(e) => setResponsable(e.target.value)}
            className="w-full h-12 bg-neutral-900 border border-neutral-800 rounded-md px-4 text-neutral-50 placeholder:text-neutral-600 focus:border-amber-400 focus:outline-none text-sm"
          />
        </div>
      </div>

      {/* Search */}
      <div className="px-4 py-3 border-b border-neutral-800 bg-neutral-950">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
          <input
            data-testid="inv-input-search"
            type="text"
            placeholder="Buscar producto..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-11 bg-neutral-900 border border-neutral-800 rounded-md pl-9 pr-9 text-neutral-50 placeholder:text-neutral-600 focus:border-amber-400 focus:outline-none text-sm"
          />
          {searchQuery && (
            <button
              data-testid="inv-btn-clear-search"
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <X className="w-4 h-4 text-neutral-500" />
            </button>
          )}
        </div>
      </div>

      {/* Column headers */}
      <div className="px-4 py-2 flex items-center bg-neutral-950 border-b border-neutral-800">
        <div className="flex-1 text-xs font-semibold tracking-widest uppercase text-neutral-500">
          Producto
        </div>
        <div className="w-24 text-center text-xs font-semibold tracking-widest uppercase text-neutral-500">
          Cantidad actual
        </div>
      </div>

      {/* Product list */}
      <div className="flex-1 overflow-y-auto pb-28">
        {products.length === 0 ? (
          <div className="px-4 py-14 text-center">
            <p className="text-neutral-400 text-sm">Sin productos configurados.</p>
            <p className="text-neutral-600 text-xs mt-1">
              Ve a Administración para agregar productos de inventario.
            </p>
          </div>
        ) : filterByZona && !selectedZona ? (
          <div className="px-4 py-14 text-center">
            <p className="text-neutral-400 text-sm">Selecciona una zona</p>
            <p className="text-neutral-600 text-xs mt-1">
              Elige una zona para ver sus productos.
            </p>
          </div>
        ) : filterByZona && zonaProducts.length === 0 ? (
          <div className="px-4 py-14 text-center">
            <p className="text-neutral-400 text-sm">Sin productos para esta zona.</p>
            <p className="text-neutral-600 text-xs mt-1">
              Agrega productos asignados a esta zona en Administración.
            </p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="px-4 py-10 text-center text-neutral-500 text-sm">
            No se encontraron productos con ese nombre.
          </div>
        ) : (
          filteredProducts.map((product) => {
            const qty = quantities[product.id];
            const hasQty = qty !== undefined && qty !== "" && Number(qty) > 0;
            return (
              <div
                key={product.id}
                data-testid={`inv-product-row-${product.id}`}
                className={`flex items-center px-4 py-3 border-b border-neutral-800 transition-colors duration-150 ${
                  hasQty ? "bg-amber-950/20" : ""
                }`}
              >
                <div className="flex-1 min-w-0 pr-3">
                  <div
                    className={`text-sm font-medium truncate ${
                      hasQty ? "text-amber-400" : "text-neutral-50"
                    }`}
                  >
                    {product.name}
                  </div>
                  <div className="text-xs text-neutral-500 font-mono-data">
                    {product.unit}
                  </div>
                </div>
                <input
                  data-testid={`inv-input-cantidad-${product.id}`}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  enterKeyHint="next"
                  value={quantities[product.id] ?? ""}
                  onChange={(e) => updateQuantity(product.id, e.target.value)}
                  placeholder="0"
                  className={`w-24 h-11 border rounded text-center text-sm font-mono-data focus:outline-none transition-colors duration-150 ${
                    hasQty
                      ? "bg-amber-950 border-amber-400/60 text-amber-400 focus:border-amber-400"
                      : "bg-neutral-800 border-neutral-700 text-neutral-50 focus:border-neutral-600"
                  }`}
                />
              </div>
            );
          })
        )}
      </div>

      {/* Export footer */}
      <div
        className="fixed bottom-0 left-0 right-0 px-4 pt-3 bg-neutral-950/95 backdrop-blur border-t border-neutral-800 max-w-md mx-auto w-full z-40"
        style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}
      >
        <button
          data-testid="export-excel-button"
          onClick={exportExcel}
          disabled={zonaProducts.length === 0 || isExporting}
          className={`w-full h-14 rounded-md font-semibold text-base flex items-center justify-center gap-2 transition-colors duration-150 ${
            zonaProducts.length > 0 && !isExporting
              ? "bg-amber-400 text-neutral-950 active:bg-amber-300"
              : "bg-neutral-800 text-neutral-600 cursor-not-allowed"
          }`}
        >
          <Download className="w-5 h-5" />
          {isExporting
            ? "Generando Excel..."
            : zonaProducts.length > 0
            ? `Exportar a Excel (${zonaProducts.length} producto${zonaProducts.length !== 1 ? "s" : ""})`
            : "Sin productos para esta zona"}
        </button>
      </div>
    </div>
  );
};

export default InventarioPage;
